import { Controller } from "@hotwired/stimulus"

const DEFAULT_DELAY = 12_000

export default class extends Controller {
  static values = {
    autohide: { type: Boolean, default: true },
    delay: { type: Number, default: DEFAULT_DELAY },
    removeDelay: { type: Number, default: 180 },
    hiddenClass: { type: String, default: "ec-flash--hidden" }
  }
  static targets = ["message"]

  initialize() {
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this)
  }

  connect() {
    if (this.element.classList.contains(this.hiddenClassValue)) {
      this.dismissed = true
      this.element.remove()
      return
    }

    this.dismissed = false
    this.dismissTimer = null
    this.removeTimer = null
    this.timerStartedAt = null
    this.remainingDelay = Math.max(this.delayValue, 0)
    this.pauseState = {
      hover: false,
      focus: this.element.contains(document.activeElement),
      hidden: document.visibilityState === "hidden"
    }
    this.autoDismiss = this.autohideValue && !this.hasInteractiveMessageContent()

    document.addEventListener("visibilitychange", this.handleVisibilityChange)
    this.startTimer()
  }

  disconnect() {
    this.clearTimers()
    document.removeEventListener("visibilitychange", this.handleVisibilityChange)
  }

  pause() {
    this.setPauseState("hover", true)
  }

  resume() {
    this.setPauseState("hover", false)
  }

  focusIn() {
    this.setPauseState("focus", true)
  }

  focusOut(event) {
    if (event.relatedTarget && this.element.contains(event.relatedTarget)) return

    this.setPauseState("focus", false)
  }

  dismiss(event) {
    if (event) event.preventDefault()
    if (this.dismissed) return

    this.dismissed = true
    this.clearTimers()
    this.element.classList.add(this.hiddenClassValue)

    this.removeTimer = window.setTimeout(() => {
      this.element.remove()
    }, this.removeDelayValue)
  }

  startTimer() {
    if (!this.autoDismiss || this.dismissed || this.hasActivePause()) return
    if (this.remainingDelay <= 0) return this.dismiss()
    if (this.dismissTimer !== null) return

    this.timerStartedAt = Date.now()
    this.dismissTimer = window.setTimeout(() => {
      this.dismiss()
    }, this.remainingDelay)
  }

  clearTimers() {
    if (this.dismissTimer !== null) window.clearTimeout(this.dismissTimer)
    if (this.removeTimer !== null) window.clearTimeout(this.removeTimer)

    this.dismissTimer = null
    this.removeTimer = null
    this.timerStartedAt = null
  }

  handleVisibilityChange() {
    this.setPauseState("hidden", document.visibilityState === "hidden")
  }

  hasInteractiveMessageContent() {
    const message = this.hasMessageTarget ? this.messageTarget : this.element.querySelector(".ec-flash__message")
    if (!message) return false

    return Boolean(message.querySelector([
      "a[href]",
      "button",
      "input:not([type='hidden'])",
      "select",
      "textarea",
      "summary",
      "[contenteditable='true']",
      "[tabindex]:not([tabindex='-1'])"
    ].join(",")))
  }

  setPauseState(reason, paused) {
    if (!this.autoDismiss || this.dismissed || this.pauseState[reason] === paused) return

    const wasPaused = this.hasActivePause()
    this.pauseState[reason] = paused

    if (paused && !wasPaused) {
      this.stopActiveTimer()
    } else if (!paused && !this.hasActivePause()) {
      this.startTimer()
    }
  }

  hasActivePause() {
    return Object.values(this.pauseState).some(Boolean)
  }

  stopActiveTimer() {
    if (this.dismissTimer === null) return

    window.clearTimeout(this.dismissTimer)
    this.dismissTimer = null

    const elapsed = Date.now() - this.timerStartedAt
    this.remainingDelay = Math.max(this.remainingDelay - elapsed, 0)
    this.timerStartedAt = null
  }
}
